import { useId, useRef, useState, type KeyboardEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PopoverContent } from '@/components/ui/popover';
import { normaliseUrl } from './markdownCommands';

export interface LinkFormProps {
  /** The selected words, which become the link's text. Empty when nothing was selected. */
  initialText: string;
  /** Called with the link's words and an address `normaliseUrl` accepted. */
  onAdd: (text: string, url: string) => void;
  onCancel: () => void;
  /** Where focus goes when the form closes, by any route: the text being written. */
  returnFocusTo: () => HTMLElement | null;
}

/** Said when an address cannot be linked (spec 145 US1 scenario 5). */
export const REFUSED_ADDRESS = 'Only web and email addresses can be linked.';

/**
 * The "Add link" form, in a popover over the toolbar's Link button (spec 145
 * FR-003). Rendered inside the editor's `Popover`, which owns whether it is
 * open; this owns what is typed into it.
 *
 * **URL first when words were selected.** Selecting "the spec" and pressing
 * Ctrl/⌘+K says what the link is called; the one thing still missing is where
 * it goes, so that is where the caret waits. With nothing selected both are
 * asked for, the words first, in reading order.
 *
 * **Not a `<form>`.** The popover is portalled, but React still bubbles a
 * submit through its own tree, and the create-task dialog's form is above it
 * there: an inner submit would have created the task. Enter is handled on the
 * two fields instead, which is all a form element would have added.
 *
 * Esc is the popover's own: it closes, and `returnFocusTo` puts the caret back
 * in the text, where it was.
 */
export function LinkForm({
  initialText,
  onAdd,
  onCancel,
  returnFocusTo,
}: LinkFormProps) {
  const titleId = useId();
  const textId = useId();
  const urlId = useId();
  const errorId = useId();
  const [text, setText] = useState(initialText);
  const [url, setUrl] = useState('');
  const [refused, setRefused] = useState(false);
  const textField = useRef<HTMLInputElement>(null);
  const urlField = useRef<HTMLInputElement>(null);

  const add = () => {
    const address = normaliseUrl(url);
    if (address === null) {
      setRefused(true);
      urlField.current?.focus();
      return;
    }
    onAdd(text, address);
  };

  const onEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (url.trim() !== '') add();
  };

  return (
    <PopoverContent
      aria-labelledby={titleId}
      className="w-72 p-3"
      initialFocus={initialText.trim() === '' ? textField : urlField}
      finalFocus={returnFocusTo}
    >
      <div className="flex flex-col gap-3">
        <h2 id={titleId} className="text-sm font-medium">
          Add link
        </h2>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={textId}>Text</Label>
          <Input
            id={textId}
            ref={textField}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
            }}
            onKeyDown={onEnter}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={urlId}>URL</Label>
          <Input
            id={urlId}
            ref={urlField}
            // `text`, not `url`: a bare host or an email address is welcome
            // here and made into an address on Add, and `type="url"` would
            // have the browser refuse both first.
            type="text"
            inputMode="url"
            autoComplete="url"
            spellCheck={false}
            placeholder="https://"
            value={url}
            aria-invalid={refused || undefined}
            aria-describedby={refused ? errorId : undefined}
            onChange={(event) => {
              setUrl(event.target.value);
              setRefused(false);
            }}
            onKeyDown={onEnter}
          />
          {refused ? (
            <p id={errorId} role="alert" className="text-destructive text-xs">
              {REFUSED_ADDRESS}
            </p>
          ) : null}
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={url.trim() === ''}
            onClick={add}
          >
            Add link
          </Button>
        </div>
      </div>
    </PopoverContent>
  );
}
